/**
 * Hand Strength Calculation Module
 *
 * Utilities for declaration selection in bidding decisions.
 */

import type {
  Bid,
  Domino,
  GameAction,
  RegularSuit,
  TrumpSelection,
} from '../types';
import {
  countDoubles,
  getDominoPoints,
} from '../core/dominoes';
import {
  CALLED_SUIT,
  DOMINO_PIPS,
  EFFECTIVE_SUIT,
  HAS_POWER,
  RANK,
  SUIT_MASK,
  dominoToId,
  getAbsorptionId,
  getPowerId,
  handToBitmask,
} from '../core/domino-tables';

export type StandardDeclaration =
  | { type: 'suit'; suit: RegularSuit }
  | { type: 'doubles' }
  | { type: 'no-trump' };

export interface DeclarationContext {
  bid: Bid;
}

export interface DeclarationEvaluation {
  declaration: StandardDeclaration;
  score: number;
  control: number;
  flexibility: number;
  countSecurity: number;
  trumpContinuity: number;
  risk: number;
  trumpCommand: number;
}

export interface RankedDeclarationAction {
  action: Extract<GameAction, { type: 'select-trump' }>;
  evaluation: DeclarationEvaluation;
}

export type DeclarationChoiceReason =
  | 'highest-score'
  | 'nello-low-hand'
  | 'sevens-concentration'
  | 'fallback';

export interface DeclarationDecision {
  action: Extract<GameAction, { type: 'select-trump' }>;
  ranked: RankedDeclarationAction[];
  reason: DeclarationChoiceReason;
  contractTarget: number;
}

const ALL_DOMINOES_MASK = (1 << DOMINO_PIPS.length) - 1;
const FLEXIBILITY_WEIGHT = 0.22;
const COUNT_SECURITY_WEIGHT = 0.32;
const TRUMP_CONTINUITY_WEIGHT = 0.48;
const TRUMP_COMMAND_WEIGHT = 1;
const NO_TRUMP_CASHABILITY = 0.68;
const NO_TRUMP_LEAD_EXPOSURE_WEIGHT = 0.22;
const DECLARATION_RISK_WEIGHT = 0.75;

interface DominoControlProfile {
  domino: Domino;
  primary: number;
  secondary: number;
  hasPower: boolean;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function countBits(mask: number): number {
  let remaining = mask;
  let count = 0;
  while (remaining !== 0) {
    remaining &= remaining - 1;
    count += 1;
  }
  return count;
}

function bitToDominoId(bit: number): number {
  return 31 - Math.clz32(bit);
}

function controlInSuit(
  dominoId: number,
  suit: number,
  handMask: number,
  absorptionId: number,
  powerId: number,
): number {
  if ((SUIT_MASK[absorptionId]?.[suit] ?? 0) === 0) return 0;

  let unseenFollowers =
    (SUIT_MASK[absorptionId]?.[suit] ?? 0) &
    ~handMask &
    ALL_DOMINOES_MASK;
  let higherFollowers = 0;
  const dominoRank = RANK[dominoId]?.[powerId] ?? 0;

  while (unseenFollowers !== 0) {
    const nextBit = unseenFollowers & -unseenFollowers;
    const otherId = bitToDominoId(nextBit);
    if ((RANK[otherId]?.[powerId] ?? 0) > dominoRank) {
      higherFollowers += 1;
    }
    unseenFollowers ^= nextBit;
  }

  return 1 / (1 + higherFollowers);
}

function buildControlProfiles(
  hand: Domino[],
  declaration: StandardDeclaration,
  riskExponent: number,
): DominoControlProfile[] {
  const absorptionId = getAbsorptionId(declaration);
  const powerId = getPowerId(declaration);
  const handMask = handToBitmask(hand);

  return hand.map((domino) => {
    const dominoId = dominoToId(domino);
    const leadSuit = EFFECTIVE_SUIT[dominoId]?.[absorptionId] ?? domino.high;
    const controls: Array<{ suit: number; value: number }> = [];

    for (let suit = 0; suit <= CALLED_SUIT; suit += 1) {
      if (((SUIT_MASK[absorptionId]?.[suit] ?? 0) & (1 << dominoId)) === 0) {
        continue;
      }
      controls.push({
        suit,
        value: Math.pow(
          controlInSuit(dominoId, suit, handMask, absorptionId, powerId),
          riskExponent,
        ),
      });
    }

    const primary =
      controls.find(({ suit }) => suit === leadSuit)?.value ?? 0;
    const secondary = controls.reduce(
      (best, { suit, value }) => suit === leadSuit ? best : Math.max(best, value),
      0,
    );

    return {
      domino,
      primary,
      secondary,
      hasPower: HAS_POWER[dominoId]?.[powerId] ?? false,
    };
  });
}

export function contractTargetForBid(bid: Bid): number {
  if (bid.type === 'pass') {
    throw new Error('Declaration policy requires the winning non-pass bid.');
  }
  if (bid.type === 'points') {
    if (bid.value === undefined || !Number.isFinite(bid.value)) {
      throw new Error('Point bid requires a finite value.');
    }
    return clamp(bid.value, 30, 42);
  }
  return 42;
}

/**
 * Compare one standard declaration using continuous hand-structure features.
 *
 * Double count is only trump depth. It never selects doubles by itself. Exact
 * double ranks, control after suit absorption, side-suit flexibility, count
 * security, and the competing declaration's structure all enter the score.
 */
export function evaluateDeclarationStrength(
  hand: Domino[],
  declaration: StandardDeclaration,
  context: DeclarationContext,
): DeclarationEvaluation {
  const target = contractTargetForBid(context.bid);
  const contractPressure = clamp((target - 30) / 12, 0, 1);
  const riskExponent = 1 + (0.9 * contractPressure);
  const profiles = buildControlProfiles(hand, declaration, riskExponent);

  let control = 0;
  let flexibility = 0;
  let trumpContinuity = 0;
  let risk = 0;
  let trumpCommand = 0;
  let countCashability: number[] = [];

  if (declaration.type === 'no-trump') {
    control = profiles.reduce(
      (total, profile) => total + (profile.primary * NO_TRUMP_CASHABILITY),
      0,
    );
    flexibility = profiles.reduce(
      (total, profile) => total + (profile.secondary * FLEXIBILITY_WEIGHT),
      0,
    );
    risk = profiles.reduce(
      (total, profile) =>
        total + ((1 - profile.primary) * NO_TRUMP_LEAD_EXPOSURE_WEIGHT),
      0,
    );
    countCashability = profiles.map((profile) =>
      Math.max(
        profile.primary * NO_TRUMP_CASHABILITY,
        profile.secondary * FLEXIBILITY_WEIGHT,
      ),
    );
  } else {
    const absorptionId = getAbsorptionId(declaration);
    const totalTrumpCount = countBits(
      SUIT_MASK[absorptionId]?.[CALLED_SUIT] ?? 0,
    );
    const trumpProfiles = profiles.filter(({ hasPower }) => hasPower);
    const trumpDepthShare = totalTrumpCount > 0
      ? trumpProfiles.length / totalTrumpCount
      : 0;
    const trumpQuality = trumpProfiles.length > 0
      ? trumpProfiles.reduce((total, profile) => total + profile.primary, 0) /
        trumpProfiles.length
      : 0;

    trumpCommand = trumpDepthShare * trumpQuality;
    const offCashability =
      TRUMP_COMMAND_WEIGHT * Math.sqrt(trumpCommand);

    control = profiles.reduce(
      (total, profile) =>
        total + (profile.hasPower
          ? profile.primary
          : profile.primary * offCashability),
      0,
    );
    flexibility = profiles.reduce(
      (total, profile) =>
        total + (profile.hasPower
          ? 0
          : profile.secondary * FLEXIBILITY_WEIGHT),
      0,
    );

    const trumpControl = trumpProfiles.reduce(
      (total, profile) => total + profile.primary,
      0,
    );
    const continuityScale = Math.max(
      0,
      Math.log2(1 + trumpProfiles.length) - 1,
    );
    trumpContinuity =
      trumpControl * continuityScale * TRUMP_CONTINUITY_WEIGHT;
    risk =
      (1 - trumpCommand) *
      (1 - trumpDepthShare) *
      DECLARATION_RISK_WEIGHT *
      (1 + contractPressure);

    countCashability = profiles.map((profile) =>
      profile.hasPower
        ? profile.primary
        : Math.max(
            profile.primary * offCashability,
            profile.secondary * FLEXIBILITY_WEIGHT,
          ),
    );
  }

  const countPressure = 0.6 + (0.4 * contractPressure);
  const countSecurity = profiles.reduce((total, profile, index) => {
    const points = getDominoPoints(profile.domino);
    if (points === 0) return total;
    const cashability = countCashability[index] ?? 0;
    return total +
      ((points / 5) *
        ((2 * cashability) - 1) *
        COUNT_SECURITY_WEIGHT *
        countPressure);
  }, 0);

  return {
    declaration,
    score: control + flexibility + countSecurity + trumpContinuity - risk,
    control,
    flexibility,
    countSecurity,
    trumpContinuity,
    risk,
    trumpCommand,
  };
}

function declarationTieBreakValue(declaration: StandardDeclaration): number {
  if (declaration.type === 'suit') return 100 + declaration.suit;
  if (declaration.type === 'no-trump') return 10;
  return 0;
}

export function compareDeclarationEvaluations(
  left: Pick<DeclarationEvaluation, 'declaration' | 'score' | 'risk' | 'control'>,
  right: Pick<DeclarationEvaluation, 'declaration' | 'score' | 'risk' | 'control'>,
): number {
  return (
    right.score - left.score ||
    left.risk - right.risk ||
    right.control - left.control ||
    declarationTieBreakValue(right.declaration) -
      declarationTieBreakValue(left.declaration)
  );
}

function standardDeclaration(
  trump: TrumpSelection,
): StandardDeclaration | undefined {
  if (trump.type === 'suit' && trump.suit !== undefined) {
    return { type: 'suit', suit: trump.suit };
  }
  if (trump.type === 'doubles' || trump.type === 'no-trump') {
    return { type: trump.type };
  }
  return undefined;
}

export function rankDeclarationActions(
  hand: Domino[],
  actions: GameAction[],
  context: DeclarationContext,
): RankedDeclarationAction[] {
  return actions
    .filter(
      (action): action is Extract<GameAction, { type: 'select-trump' }> =>
        action.type === 'select-trump',
    )
    .flatMap((action) => {
      const declaration = standardDeclaration(action.trump);
      return declaration
        ? [{
            action,
            evaluation: evaluateDeclarationStrength(hand, declaration, context),
          }]
        : [];
    })
    .sort((left, right) =>
      compareDeclarationEvaluations(left.evaluation, right.evaluation),
    );
}

/**
 * Select a declaration from the engine-supplied legal actions.
 *
 * This is shared by live AI decisions and bidding rollouts so the bidder
 * evaluates the declaration policy it will actually use.
 */
export function evaluateDeclarationActions(
  hand: Domino[],
  actions: GameAction[],
  context: DeclarationContext,
): DeclarationDecision | undefined {
  const declarations = actions.filter(
    (action): action is Extract<GameAction, { type: 'select-trump' }> =>
      action.type === 'select-trump',
  );
  if (declarations.length === 0) return undefined;
  const ranked = rankDeclarationActions(hand, declarations, context);
  const contractTarget = contractTargetForBid(context.bid);
  const sevens = declarations.find(({ trump }) => trump.type === 'sevens');
  const distancesFromSeven = hand.map(
    ({ high, low }) => Math.abs(7 - (high + low)),
  );
  const exactSevens = distancesFromSeven.filter((distance) => distance === 0).length;
  if (
    sevens &&
    exactSevens >= 3 &&
    distancesFromSeven.every((distance) => distance <= 1)
  ) {
    return { action: sevens, ranked, reason: 'sevens-concentration', contractTarget };
  }

  const nello = declarations.find(({ trump }) => trump.type === 'nello');
  const highestPipTotal = hand.reduce(
    (highest, { high, low }) => Math.max(highest, high + low),
    0,
  );
  if (nello && countDoubles(hand) === 0 && highestPipTotal <= 5) {
    return { action: nello, ranked, reason: 'nello-low-hand', contractTarget };
  }

  const highest = ranked[0]?.action;
  return {
    action: highest ?? declarations[0]!,
    ranked,
    reason: highest ? 'highest-score' : 'fallback',
    contractTarget,
  };
}

export function selectDeclarationAction(
  hand: Domino[],
  actions: GameAction[],
  context: DeclarationContext,
): Extract<GameAction, { type: 'select-trump' }> | undefined {
  return evaluateDeclarationActions(hand, actions, context)?.action;
}
